import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs/operators';

import { LogService, PinService } from '../../providers';
import { AuthenticationFacade, AuthSessionService } from '../auth';
import { UserData } from './interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly authenticationService = inject(AuthenticationFacade);
  private readonly authSession = inject(AuthSessionService);
  private readonly savePinService = inject(PinService);

  private readonly userData: WritableSignal<UserData | null> = signal(null);
  // Tracks the authentication state itself, separately from userData: while authenticated,
  // userData briefly stays at its previous value until the async fetch below resolves, so
  // userData alone can't tell "still loading" apart from "definitely logged out".
  private readonly authenticated = signal(false);

  public hasSecondFactor = computed(() => (this.authenticated() ? this.userData()?.hasSecondFactor : false));
  public userSecret = computed(() => this.userData()?.userSecret);
  public username = computed(() => this.userData()?.username);

  constructor() {
    this.authenticationService.authenticationChange$
      .pipe(
        takeUntilDestroyed(),
        tap((isAuthenticated) => {
          this.authenticated.set(isAuthenticated);
          if (!isAuthenticated) {
            this.userData.set(null);
          }
        }),
        filter((isAuthenticated) => isAuthenticated),
        tap(async () => {
          try {
            const userData = await this.authSession.getUserData();
            this.userData.set(userData);
          } catch (error) {
            LogService.error(`${JSON.stringify(error)}`);
            this.userData.set(null);
          }
        }),
      )
      .subscribe();
  }

  public async getSecondFactor(): Promise<string | null> {
    const username = this.username();
    const userSecret = this.userSecret();

    if (!username) {
      return Promise.resolve(null);
    }

    if (!userSecret) {
      return Promise.resolve(null);
    }

    return await this.savePinService.getPin(username, userSecret);
  }

  public resetSecondFactor(): Promise<void> {
    const username = this.username();

    if (!username) {
      return Promise.resolve();
    }

    return this.savePinService.resetPin(username);
  }

  public setSecondFactor(secondFactor: string): Promise<void> {
    const username = this.username();
    const userSecret = this.userSecret();

    if (!username) {
      return Promise.resolve();
    }

    if (!userSecret) {
      return Promise.resolve();
    }

    return this.savePinService.setPin(username, userSecret, secondFactor);
  }
}
